import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProtectedAction } from '../hooks/useProtectedAction';
import { auth } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import { useLeads } from '../context/LeadsContext';

const N8N = 'https://fatima.agenciafalei.com.br/webhook';

export default function ExtractorMaps() {
  const [nicho, setNicho]       = useState('');
  const [cidade, setCidade]     = useState('');
  const [bairro, setBairro]     = useState('');
  const [cobertura, setCobertura] = useState(null);
  const [saldo, setSaldo]       = useState(null);
  // estilo inline não tem media query: acompanha a largura para empilhar no celular
  const [estreito, setEstreito] = useState(typeof window !== 'undefined' && window.innerWidth < 1100);
  useEffect(() => {
    const aoRedimensionar = () => setEstreito(window.innerWidth < 1100);
    window.addEventListener('resize', aoRedimensionar);
    return () => window.removeEventListener('resize', aoRedimensionar);
  }, []);
  const [loading, setLoading]   = useState(false);
  const [status, setStatus]     = useState('');
  const [leads, setLeads]       = useState([]);
  const [selected, setSelected] = useState([]);

  const { protect, Modals, logado } = useProtectedAction();
  // extrai PARA a conta: o lead que o vendedor trouxer entra na base do dono
  const { contaUid } = useAuth();
  const { setParaKanban, setParaDisparo } = useLeads();
  const navigate = useNavigate();

  const extrair = protect(async () => {
    if (!nicho || !cidade) { setStatus('⚠ Preencha nicho e cidade.'); return; }
    setLoading(true); setStatus(''); setLeads([]); setSelected([]);
    try {
      const uid = contaUid;
      if (!uid) { setStatus('⚠ Faça login para extrair.'); setLoading(false); return; }
      const r = await fetch(`${N8N}/extrator-maps`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nicho, cidade, bairro, uid })
      });
      const data = await r.json();
      if (!data.ok) throw new Error(data.erro || 'Erro na extração');
      if (data.bloqueado) {
        setStatus('⚠ ' + data.aviso);
        setSaldo({ parados: data.leadsParados, limite: 400 });
        return;
      }
      setLeads(data.places || []);
      if (typeof data.leadsParados === 'number') setSaldo({ parados: data.leadsParados, limite: data.limiteParados || 400 });
      const msgs = [];
      if (data.buscasFeitas) msgs.push(`${data.buscasFeitas} buscas`);
      if (data.filtradosDDD > 0) msgs.push(`${data.filtradosDDD} de outras regiões removidos`);
      if (data.jaNoHistorico > 0) msgs.push(`${data.jaNoHistorico} já extraídos antes`);
      // 0 leads sem explicação era o que mais virava chamado
      setStatus(data.total === 0
        ? `⚠ Nenhum lead novo. ${data.aviso || ''}`
        : `✅ ${data.total} leads novos${msgs.length ? ' · ' + msgs.join(' · ') : ''}`);
      carregarCobertura();
    } catch(e) {
      setStatus('⚠ ' + e.message);
    } finally { setLoading(false); }
  });

  // Cobertura: le as extracoes ja feitas e monta o mapa de bairros varridos.
  const carregarCobertura = useCallback(async () => {
    const u = auth.currentUser;
    if (!u || !contaUid) return;
    try {
      const token = await u.getIdToken();
      const r = await fetch(
        `https://firestore.googleapis.com/v1/projects/migravip-e1afb/databases/(default)/documents/users/${contaUid}/extracoes?pageSize=300`,
        { headers: { Authorization: 'Bearer ' + token } });
      const j = await r.json();
      const porCidade = {};
      for (const doc of (j.documents || [])) {
        const f = doc.fields || {};
        const cid = (f.cidade?.stringValue || '').replace(/,.*$/, '').trim();
        if (!cid) continue;
        const total = parseInt(f.total?.integerValue || 0, 10);
        let bairros = [];
        try { bairros = JSON.parse(f.bairrosVarridos?.stringValue || '[]'); } catch { bairros = []; }
        if (f.bairro?.stringValue) bairros = [f.bairro.stringValue];
        const c = porCidade[cid] || (porCidade[cid] = { cidade: cid, bairros: {}, total: 0, extracoes: 0 });
        c.total += total; c.extracoes += 1;
        const fatia = bairros.length ? Math.round(total / bairros.length) : 0;
        for (const b of bairros) c.bairros[b] = (c.bairros[b] || 0) + fatia;
      }
      setCobertura(Object.values(porCidade).sort((a, b) => b.total - a.total));
    } catch { /* cobertura e complementar: falha nao atrapalha a extracao */ }
  }, [contaUid]);

  useEffect(() => { carregarCobertura(); }, [carregarCobertura]);

  const toggleSelect = (i) =>
    setSelected(prev => prev.includes(i) ? prev.filter(x=>x!==i) : [...prev, i]);
  const toggleAll = () =>
    setSelected(selected.length === leads.length ? [] : leads.map((_,i)=>i));

  const selecionadosLeads = () => selected.map(i => leads[i]).filter(Boolean);

  const irParaKanban = () => {
    const s = selecionadosLeads();
    if (!s.length) return;
    setParaKanban(s);
    navigate('/kanban');
  };
  const irParaDisparo = () => {
    const s = selecionadosLeads();
    if (!s.length) return;
    setParaDisparo(s);
    navigate('/disparo');
  };

  const exportCSV = () => {
    const rows = [['Nome','Telefone','Tipo','Endereço','Nota']];
    leads.forEach(l => rows.push([l.nome, l.telefone, l.tipo, l.endereco, l.nota]));
    const csv = rows.map(r => r.map(v=>`"${v}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    a.download = `leads_${nicho}_${bairro || cidade}.csv`;
    a.click();
  };

  return (
    <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
      {Modals}

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: '26px',
          fontWeight: '800', letterSpacing: '-0.5px', marginBottom: '6px' }}>
          Extrator Google Maps
        </h1>
        <p style={{ color: '#52526e', fontSize: '13px' }}>
          Varre bairro a bairro e traz todos os leads novos que encontrar.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: estreito ? '1fr' : 'minmax(0,1fr) 340px',
        gap: '20px', alignItems: 'start' }}>

        {/* esquerda: extrair e resultado */}
        <div style={{ minWidth: 0 }}>
        <div style={card}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={lbl}>Nicho</label>
              <input value={nicho} onChange={e=>setNicho(e.target.value)}
                placeholder="Ex: Podologia, Salão de Beleza, Barbearia"
                style={inp} />
            </div>
            <div>
              <label style={lbl}>Cidade</label>
              <input value={cidade} onChange={e=>setCidade(e.target.value)}
                placeholder="Ex: Curitiba, PR" style={inp} />
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={lbl}>Bairro <span style={{ opacity: .6, textTransform: 'none' }}>(opcional)</span></label>
            <input value={bairro} onChange={e=>setBairro(e.target.value)}
              placeholder="Ex: Batel, Moema, Savassi — vazio varre vários bairros da cidade"
              style={inp} />
            <div style={{ fontSize: '11px', color: '#52526e', marginTop: '6px' }}>
              O Google corta por raio, então ir bairro a bairro é o que traz gente nova quando a
              cidade já foi varrida. Leads repetidos são descartados automaticamente.
            </div>
          </div>

          {!logado && (
            <div style={{
              background: 'rgba(108,71,255,0.06)', border: '1px solid rgba(108,71,255,0.2)',
              borderRadius: '10px', padding: '12px 16px', marginBottom: '16px',
              fontSize: '13px', color: '#6c47ff', display: 'flex',
              alignItems: 'center', gap: '8px'
            }}>
              🔒 Entre para usar o extrator e ver seus leads
            </div>
          )}

          {status && (
            <div style={{
              padding: '10px 14px', borderRadius: '9px', marginBottom: '14px',
              fontSize: '12px', fontFamily: 'monospace',
              background: status.startsWith('✅') ? 'rgba(16,185,129,0.08)'
                        : status.startsWith('⚠') ? 'rgba(245,158,11,0.09)' : 'rgba(239,68,68,0.08)',
              color: status.startsWith('✅') ? '#047857' : status.startsWith('⚠') ? '#b45309' : '#dc2626',
              border: `1px solid ${status.startsWith('✅') ? 'rgba(16,185,129,0.2)'
                        : status.startsWith('⚠') ? 'rgba(245,158,11,0.3)' : 'rgba(239,68,68,0.2)'}`
            }}>{status}</div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '12px', color: '#52526e' }}>
              Traz tudo o que encontrar {bairro ? <>em <strong>{bairro}</strong></> : 'nos bairros varridos'}, sem repetir quem já saiu antes.
              {saldo && (
                <div style={{ marginTop: '6px', fontSize: '11px',
                  color: saldo.parados >= saldo.limite * 0.75 ? '#b45309' : '#52526e' }}>
                  {saldo.parados} de {saldo.limite} leads aguardando disparo
                  {saldo.parados >= saldo.limite * 0.75 && ' — agende os que já tem para liberar espaço'}
                </div>
              )}
            </span>
            <button onClick={extrair} disabled={loading}
              style={{
                background: loading ? '#9898b8' : 'linear-gradient(135deg, #6c47ff, #5535e0)',
                color: '#fff', border: 'none', borderRadius: '10px',
                padding: '11px 28px', fontSize: '14px', fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}>
              {loading ? '⟳ Extraindo...' : logado ? (bairro ? `Extrair ${bairro} →` : 'Extrair leads →') : '🔒 Entrar para extrair'}
            </button>
          </div>
        </div>

        {leads.length > 0 && (
          <div style={{ ...card, marginTop: '20px', padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(0,0,0,0.07)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
              <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '14px' }}>
                {'📋 ' + leads.length + ' leads extraídos'}
              </span>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button onClick={exportCSV} style={btnGhost}>{'↓ CSV'}</button>
                {selected.length > 0 && (
                  <>
                    <button onClick={irParaKanban} style={btnGhost}>
                      {'＋ Criar card no Kanban'}
                    </button>
                    <button onClick={irParaDisparo} style={{
                      background: 'linear-gradient(135deg, #6c47ff, #5535e0)',
                      color: '#fff', border: 'none', borderRadius: '8px',
                      padding: '7px 16px', fontSize: '12px', fontWeight: '600', cursor: 'pointer'
                    }}>
                      {'🚀 Disparar ' + selected.length + ' →'}
                    </button>
                  </>
                )}
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#f8f8fc' }}>
                  <th style={th}><input type="checkbox"
                    checked={selected.length===leads.length} onChange={toggleAll} /></th>
                  <th style={th}>Nome</th>
                  <th style={th}>Telefone</th>
                  <th style={th}>Tipo</th>
                  <th style={th}>Endereço</th>
                  <th style={th}>Nota</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l, i) => (
                  <tr key={i} style={{
                    borderTop: '1px solid rgba(0,0,0,0.05)',
                    background: selected.includes(i) ? 'rgba(108,71,255,0.03)' : 'transparent'
                  }}>
                    <td style={td}><input type="checkbox"
                      checked={selected.includes(i)} onChange={() => toggleSelect(i)} /></td>
                    <td style={{ ...td, fontWeight: '500', maxWidth: '180px',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {l.nome}
                    </td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{l.telefone}</td>
                    <td style={td}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '6px',
                        fontSize: '10px', fontWeight: '600',
                        background: l.tipo==='Celular' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
                        color: l.tipo==='Celular' ? '#059669' : '#d97706'
                      }}>{l.tipo}</span>
                    </td>
                    <td style={{ ...td, color: '#52526e', maxWidth: '200px',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {l.endereco}
                    </td>
                    <td style={td}>{'⭐ ' + l.nota}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
        </div>

        {/* direita: cobertura, acompanha a rolagem */}
        <div style={{ position: estreito ? 'static' : 'sticky', top: '20px' }}>
        {cobertura && cobertura.length > 0 && (
          <div style={{ ...card, padding: '18px' }}>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '800', fontSize: '15px' }}>
                🗺️ Cobertura
              </div>
              <div style={{ fontSize: '11px', color: '#52526e', marginTop: '2px' }}>
                verde é varrido, tracejado é onde ainda falta — clique para extrair só esse bairro
              </div>
            </div>

            {cobertura.slice(0, 3).map(c => {
              const varridos = Object.entries(c.bairros).sort((a, b) => b[1] - a[1]);
              const max = Math.max(1, ...varridos.map(v => v[1]));
              const sugeridos = (BAIRROS_SUGERIDOS[normCidade(c.cidade)] || [])
                .filter(b => !(b in c.bairros));
              return (
                <div key={c.cidade} style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
                    <strong style={{ fontSize: '13px' }}>{c.cidade}</strong>
                    <span style={{ fontSize: '10px', color: '#52526e', fontFamily: 'monospace' }}>
                      {c.total} leads · {varridos.length}/{varridos.length + sugeridos.length} bairros
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                    {varridos.map(([b, n]) => {
                      const forca = 0.12 + 0.55 * (n / max);   // mais leads, verde mais forte
                      return (
                        <button key={b} onClick={() => { setCidade(c.cidade); setBairro(b); }}
                          title={`${n} leads extraídos em ${b}`}
                          style={{ padding: '4px 8px', borderRadius: '6px', cursor: 'pointer',
                            border: '1px solid rgba(4,120,87,0.25)', fontSize: '11px', fontWeight: '600',
                            background: `rgba(5,150,105,${forca.toFixed(2)})`,
                            color: forca > 0.4 ? '#fff' : '#047857', fontFamily: 'DM Sans, sans-serif' }}>
                          {b} <span style={{ opacity: .85, fontSize: '10px' }}>{n}</span>
                        </button>
                      );
                    })}
                    {sugeridos.map(b => (
                      <button key={b} onClick={() => { setCidade(c.cidade); setBairro(b); }}
                        title={`${b} ainda não foi varrido`}
                        style={{ padding: '4px 8px', borderRadius: '6px', cursor: 'pointer',
                          border: '1px dashed rgba(0,0,0,0.18)', fontSize: '11px',
                          background: '#f8f8fc', color: '#6b6b85', fontFamily: 'DM Sans, sans-serif' }}>
                        {b}
                      </button>
                    ))}
                  </div>
                  {sugeridos.length > 0 && (
                    <div style={{ fontSize: '11px', color: '#52526e', marginTop: '6px' }}>
                      Faltam <strong>{sugeridos.length}</strong> bairros aqui.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        </div>
      </div>

    </div>
  );
}

// Espelha a lista do n8n: serve para mostrar quais bairros AINDA faltam.
const BAIRROS_SUGERIDOS = {
  'sao paulo': ['Pinheiros','Vila Mariana','Moema','Itaim Bibi','Tatuapé','Santana','Mooca','Lapa','Perdizes','Jardins',
                'Brooklin','Campo Belo','Santo Amaro','Ipiranga','Penha','Freguesia do Ó','Butantã','Saúde','Vila Prudente',
                'Bela Vista','Consolação','Higienópolis','Pompeia','Vila Madalena','Aclimação','Cambuci','Barra Funda'],
  'rio de janeiro': ['Copacabana','Ipanema','Barra da Tijuca','Tijuca','Botafogo','Méier','Campo Grande','Jacarepaguá',
                     'Leblon','Flamengo','Recreio','Madureira','Bangu','Vila Isabel','Santa Teresa','Icaraí'],
  'curitiba': ['Batel','Água Verde','Centro','Portão','Boa Vista','Santa Felicidade','Cabral','Bigorrilho','Rebouças',
               'Champagnat','Cristo Rei','Mercês','Hauer','Pinheirinho','Sítio Cercado','Bairro Alto'],
  'joinville': ['Centro','América','Glória','Costa e Silva','Bucarein','Atiradores','Saguaçu','Iririú','Floresta','Boa Vista'],
  'belo horizonte': ['Savassi','Funcionários','Lourdes','Buritis','Pampulha','Barreiro','Santa Efigênia','Cidade Nova',
                     'Castelo','Santo Antônio','Gutierrez','Sion','Anchieta'],
  'porto alegre': ['Moinhos de Vento','Menino Deus','Petrópolis','Centro Histórico','Bela Vista','Cidade Baixa',
                   'Tristeza','Ipanema','Higienópolis','Partenon','Zona Sul'],
  'brasilia':  ['Asa Sul','Asa Norte','Águas Claras','Taguatinga','Lago Sul','Sudoeste','Guará','Ceilândia','Samambaia'],
  'salvador':  ['Barra','Pituba','Rio Vermelho','Itaigara','Ondina','Caminho das Árvores','Imbuí','Brotas','Stella Maris'],
  'fortaleza': ['Aldeota','Meireles','Cocó','Papicu','Messejana','Parangaba','Benfica','Varjota','Edson Queiroz'],
  'recife':    ['Boa Viagem','Casa Forte','Espinheiro','Graças','Pina','Madalena','Torre','Aflitos','Setúbal'],
  'goiania':   ['Setor Bueno','Setor Oeste','Setor Marista','Jardim Goiás','Setor Sul','Campinas','Aparecida de Goiânia'],
  'campinas':  ['Cambuí','Taquaral','Barão Geraldo','Centro','Jardim Chapadão','Mansões Santo Antônio'],
  'florianopolis': ['Centro','Trindade','Lagoa da Conceição','Canasvieiras','Ingleses','Estreito','Campeche','Itacorubi'],
};
const normCidade = (c) => String(c || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

const card = { background: '#fff', border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: '14px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const lbl = { fontSize: '11px', fontWeight: '700', color: '#52526e',
  textTransform: 'uppercase', letterSpacing: '0.06em',
  fontFamily: 'monospace', display: 'block', marginBottom: '6px' };
const inp = { width: '100%', padding: '10px 12px', borderRadius: '9px',
  border: '1.5px solid rgba(0,0,0,0.1)', fontSize: '14px',
  background: '#f8f8fc', outline: 'none', boxSizing: 'border-box',
  fontFamily: 'DM Sans, sans-serif', color: '#0a0a18' };
const btnGhost = { border: '1.5px solid rgba(0,0,0,0.1)', background: 'none',
  borderRadius: '8px', padding: '7px 14px', fontSize: '12px',
  cursor: 'pointer', color: '#52526e', fontFamily: 'DM Sans, sans-serif' };
const th = { padding: '10px 14px', textAlign: 'left', fontSize: '10px',
  fontWeight: '700', color: '#9898b8', textTransform: 'uppercase',
  letterSpacing: '0.06em', fontFamily: 'monospace' };
const td = { padding: '11px 14px' };
